import CancelablePromsie from 'p-cancelable';
import SyosEvents from '../utils/SyosEvents';
import SyosServiceMethod from './SyosServiceMethod';
import SyosGlobalConfig from '../SyosGlobalConfig';
import { invoke } from './SyosApiRequest';

/** @typedef {import("../SyosController").default} SyosController */
/** @typedef {import("../../tnew/currency/CurrencyFormatter").default} CurrencyFormatter */


function getUniqueArray(value) {
  return Array.from(new Set(value).values());
}

/**
 * @typedef SeatList
 * @property {string} DefaultPrice
 * @property {number} MaxPrice
 * @property {string} MaxPriceDisplay
 * @property {number} MinPrice
 * @property {string} MinPriceDisplay
 * @property {{Price: number, ZoneNo: number}[]} AvailablePrices
 * @property {{default_price: number, screen_no: number, zone_no: number}[]} ScreenZoneList
 * @property {string[]} SeatTypes
 * @property {{price: number, zone_color: string, zone_label: string, zone_no: number}[]} ZoneColorList
 * @property {{}[]]} seats
 * @property {{section: number, section_desc: string}[]} sections
 * @property {string} stagePlacement
 */


/**
 * @typedef Pricetype
 * @property {number} PerSeatFee
 * @property {boolean} available
 * @property {string} description
 * @property {string} feeDisclaimer
 * @property {boolean} is_default
 * @property {boolean} is_promo
 * @property {number} price
 * @property {number} priceWithFee
 * @property {number} price_type
 * @property {string} price_type_desc
 * @property {number} rank
 * @property {number} zone_no
 */


/**
  * @param {Pricetype} data
  * @param {Object<string, string>} feeDisclaimers
  * @param {boolean} isFeeDisclaimerVisible
  * @param {CurrencyFormatter} currencyFormatter
  */
function massagePriceTypeData(data, feeDisclaimers, isFeeDisclaimerVisible, currencyFormatter) {
  // const PerSeatFee = Math.random() <= 0.5 ? 3 : 0;
  const PerSeatFee = data.PerSeatFee;
  let feeDisclaimer = null;

  if (isFeeDisclaimerVisible && PerSeatFee !== 0) {
    const key = `${data.price_type}-${data.zone_no}`;
    feeDisclaimer = feeDisclaimers[key];
  }

  const priceWithFee = data.price + PerSeatFee;
  const displayPrice = currencyFormatter.format(priceWithFee);

  return Object.assign({}, data, {
    PerSeatFee,
    feeDisclaimer,
    priceWithFee,
    displayPrice,
  });
}

/**
 * @param {Pricetype[]} priceTypes
 * @param {string} sortOrder
 */
function sortPriceTypes(priceTypes, sortOrder) {
  const isDescending = sortOrder.endsWith('Descending');
  const sortMode = sortOrder.replace(/(Ascending|Descending)$/, '');

  let propertyName;

  switch (sortMode) {
    case 'PriceType':
      propertyName = 'price_type';
      break;
    case 'PriceTypeDescription':
      propertyName = 'price_type_desc';
      break;
    default:
      propertyName = 'priceWithFee';
      break;
  }

  const multiplier = isDescending ? -1 : 1;

  priceTypes.sort((priceTypeA, priceTypeB) => {
    const a = priceTypeA[propertyName];
    const b = priceTypeB[propertyName];

    if (a > b) {
      return 1 * multiplier;
    }

    if (a < b) {
      return -1 * multiplier;
    }

    return 0;
  });
}

class SyosApiDataService {
  /** @type {?number} */
  defaultPriceTypeId = null;

  priceRangeIncludesAllPricetypes = true;

  /**
   * @param {SyosController} syosController
   * @param {CurrencyFormatter} currencyFormatter
   */
  constructor(syosController, currencyFormatter) {
    this.syosController = syosController;
    this.currencyFormatter = currencyFormatter;
    this.performanceDetails = null;
    this.screens = null;

    this.priceTypesByZone = null;
    this.sections = new Map();
  }

  async invoke(...args) {
    return invoke(this.syosController, ...args);
  }

  async getPerformanceDetails(performanceId) {
    if (this.performanceDetails !== null && this.performanceDetails.perf_no === performanceId) {
      return this.performanceDetails;
    }

    const data = await this.invoke(SyosServiceMethod.GetPerformanceDetails, { performanceId });

    if (!data.IsSeatMapAvailable) {
      return null;
    }

    /** @type {Map<number, Map<number, Pricetype>>} */
    const priceTypesByZone = new Map();
    const feeDisclaimers = SyosGlobalConfig.pricing.feeDisclaimers;
    const isFeeDisclaimerVisible = SyosGlobalConfig.pricing.isFeeDisclaimerVisible;

    // Remove unavailable zones from data set
    data.ScreenZoneList = data.ScreenZoneList.filter(zone => zone.zone_no !== 0);

    data.pricing = data.pricing.map(priceType => massagePriceTypeData(priceType, feeDisclaimers, isFeeDisclaimerVisible, this.currencyFormatter));

    sortPriceTypes(data.pricing, SyosGlobalConfig.priceTypeSortOrder);

    data.pricing.forEach((priceType) => {
      const priceTypeId = priceType.price_type;
      const zoneId = priceType.zone_no;

      if (priceTypesByZone.has(zoneId) === false) {
        priceTypesByZone.set(zoneId, new Map());
      }

      const zonePriceTypes = priceTypesByZone.get(zoneId);
      zonePriceTypes.set(priceTypeId, priceType);
    });

    this.priceTypesByZone = priceTypesByZone;
    this.performanceDetails = data;

    const defaultPriceType = data.pricing
      .find(el => el.is_default);

    if (defaultPriceType) {
      this.defaultPriceTypeId = defaultPriceType.price_type;
    }

    this.priceRangeIncludesAllPricetypes = this.getPriceRangeIncludesAllPriceTypes(data.ScreenZoneList, this.defaultPriceTypeId);

    return this.performanceDetails;
  }

  /**
   * @param {Array} allZones
   * @param {number} defaultPriceTypeId
   */
  getPriceRangeIncludesAllPriceTypes(allZones, defaultPriceTypeId) {
    if (SyosGlobalConfig.priceRangeIncludesAllPricetypes) {
      return true;
    }

    if (!defaultPriceTypeId) {
      return true;
    }

    const uniqueZoneIds = getUniqueArray(allZones.map(zone => zone.zone_no));

    const allZonesContainDefaultPriceType = uniqueZoneIds.every(zoneId => this.getPriceType(zoneId, defaultPriceTypeId));

    if (allZonesContainDefaultPriceType) {
      return false;
    }

    return true;
  }

  async getScreens(performanceId) {
    const data = await this.invoke(SyosServiceMethod.GetScreens, { performanceId });

    const { currencyFormatter, priceRangeIncludesAllPricetypes } = this;
    const {
      screenButtonShouldDisplayPricetypeDescription,
      screenButtonShouldRenderPriceRange,
    } = SyosGlobalConfig;

    data.forEach((screen) => {
      // add perf_no and facility_no to each screen so we don't have to
      // fetch it later during the screen clicked event
      screen.perf_no = this.performanceDetails.perf_no;
      screen.facility_no = this.performanceDetails.facility_no;

      // Clean up RedirectUrl - expecting something like "z={zone_no}"
      if (screen.RedirectUrl === 'None') {
        screen.RedirectUrl = null;
      }

      if (screen.Available) {
        if (!priceRangeIncludesAllPricetypes) {
          const pricetypes = this.getScreenPriceTypes(screen.screen_no, this.defaultPriceTypeId);

          const prices = this.getPriceRangeForPricetypes(pricetypes);

          screen.MinPrice = prices[0];
          screen.MaxPrice = prices[prices.length - 1];
        } else {
          screen.MinPrice = parseFloat(screen.MinPrice);
          screen.MaxPrice = parseFloat(screen.MaxPrice);
        }

        screen.minPriceDisplay = currencyFormatter.format(screen.MinPrice);
        screen.maxPriceDisplay = currencyFormatter.format(screen.MaxPrice);

        screen.screenButtonShouldDisplayPricetypeDescription = screenButtonShouldDisplayPricetypeDescription && !priceRangeIncludesAllPricetypes;
        screen.screenButtonShouldRenderPriceRange = screenButtonShouldRenderPriceRange;
      }
    });


    this.screens = data;

    this.syosController.emit(SyosEvents.SCREENS_DATA_LOADED, { screens: data });

    return this.screens;
  }

  /**
   * @param {number} screenId
   * @param {number} priceTypeId
   * @returns {[number]}
   */
  getScreenPriceTypes(screenId, priceTypeId) {
    /** @type {number[]} */
    const screenZoneIds = this.performanceDetails.ScreenZoneList
      .filter(screenZoneData => screenZoneData.screen_no === screenId)
      .map(screenZoneData => screenZoneData.zone_no);

    const priceTypes = screenZoneIds.map(zoneId => this.getPriceType(zoneId, priceTypeId));

    return priceTypes.filter(el => el);
  }

  /** @returns {SeatList} */
  getSeatList(performanceId, facilityNo, screenNo) {
    const promise = new CancelablePromsie((onCancel, resolve) => {
      onCancel(() => {
        this.syosController.emit(SyosEvents.API_SEAT_LIST_REQUEST_CANCELLED, {
          performanceId,
          facilityNo,
          screenNo,
        });
      });

      const request = this.invoke(
        SyosServiceMethod.GetSeatList,
        { performanceId, facilityId: facilityNo, screenId: screenNo },
        'GET',
        true,
        false,
      );

      request
        .then((data) => {
          if (promise.canceled) {
            return;
          }

          this.syosController.emit(SyosEvents.API_REQUEST_COMPLETED);

          data.sections.forEach((sectionData) => {
            this.sections.set(sectionData.section, sectionData);
          });

          this.syosController.emit(SyosEvents.SEAT_LIST_DATA_LOADED, { seatList: data });

          resolve(data);
        })
        .catch((reason) => {
          if (promise.canceled) {
            return;
          }

          throw reason;
        });
    });

    return promise;
  }

  /**
   * @param {SeatList} seatList
   */
  getLegendData(seatList) {
    const zones = seatList.ScreenZoneList
      .map((screenZoneData) => {
        const zoneId = screenZoneData.zone_no;

        const priceTypes = this.getZonePriceTypes(zoneId);

        let priceRange;

        if (this.priceRangeIncludesAllPricetypes) {
          priceRange = this.getPriceRangeForPricetypes(priceTypes);
        } else {
          priceRange = [this.getPriceType(zoneId, this.defaultPriceTypeId).priceWithFee];
        }

        const zoneColorData = seatList.ZoneColorList.find(el => el.zone_no === zoneId);

        const color = zoneColorData ? zoneColorData.zone_color : '#CCC';

        return {
          id: zoneId,
          color,
          label: priceTypes[0].description,
          priceTypes,
          priceRange,
        };
      });

    return {
      zones,
      seatTypes: seatList.SeatTypes,
    };
  }

  async reserveTempCartSeats(seats) {
    const data = this.invoke(SyosServiceMethod.ReserveTempCartSeats, { seats }, 'POST', false);

    return data;
  }

  getSection(sectionId) {
    return this.sections.get(sectionId);
  }

  /**
   * @param {number} zoneId
   * @returns {Pricetype[]}
   */
  getZonePriceTypes(zoneId) {
    const zonePriceTypes = this.priceTypesByZone.get(zoneId);

    if (zonePriceTypes) {
      return Array.from(zonePriceTypes.values());
    }

    return [];
  }

  getPriceType(zoneId, priceTypeId) {
    const zonePriceTypes = this.priceTypesByZone.get(zoneId);

    if (zonePriceTypes) {
      const priceType = zonePriceTypes.get(priceTypeId);

      if (priceType) {
        return priceType;
      }
    }

    return null;
  }

  /**
   * @param {Pricetype[]} pricetypes
   */
  getPriceRangeForPricetypes(pricetypes) {
    const allPrices = pricetypes.map(pricetype => pricetype.priceWithFee);

    if (allPrices.length === 1) {
      return allPrices;
    }

    const uniquePrices = getUniqueArray(allPrices);

    uniquePrices.sort((a, b) => a - b);

    return uniquePrices;
  }

  // 9.12.13  - HDR - TNEXWEB-2669
  getPerfAdditionalModalText() {
    if (this.performanceDetails !== null) {
      return this.performanceDetails.AdditionalText;
    }
    return '';
  }
}

export default SyosApiDataService;



// WEBPACK FOOTER //
// ./src/syos/api/SyosApiDataService.js